import type { Column, ColumnDef, VisibilityState } from '@tanstack/react-table'
import type { CrawlPage } from '../hooks/useCrawls'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ArrowUp, ArrowDown, ArrowUpDown, Check, X } from 'lucide-react'
import { decodeHtmlEntities } from '@/client/lib/decode'
import { scoreColor } from '@/client/lib/colors'

// Column group for the visibility dropdown
type ColumnGroup =
  | 'core'
  | 'title'
  | 'description'
  | 'canonical'
  | 'headings'
  | 'content'
  | 'images'
  | 'structured'
  | 'social'
  | 'indexing'
  | 'security'
  | 'redirect'
  | 'i18n'

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData, TValue> {
    group: ColumnGroup
    groupLabel: string
    sticky?: boolean
  }
}

function stripTrailingSlash(url: string): string {
  return url.endsWith('/') && url.length > 1 ? url.slice(0, -1) : url
}

// --- Sortable header helper ---

function SortableHeader({
  column,
  children,
}: {
  column: Column<CrawlPage, unknown>
  children: React.ReactNode
}) {
  return (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 text-xs"
      onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
    >
      {children}
      {column.getIsSorted() === 'asc' ? (
        <ArrowUp className="ml-1 h-3 w-3" />
      ) : column.getIsSorted() === 'desc' ? (
        <ArrowDown className="ml-1 h-3 w-3" />
      ) : (
        <ArrowUpDown className="ml-1 h-3 w-3 opacity-40" />
      )}
    </Button>
  )
}

// --- Cell renderer helpers ---

function StatusBadge({ code }: { code: number }) {
  const variant =
    code >= 500 ? 'destructive' : code >= 400 ? 'destructive' : code >= 300 ? 'secondary' : 'outline'
  return <Badge variant={variant}>{code}</Badge>
}

function BoolIcon({ value }: { value: number }) {
  return value ? (
    <Check className="h-3.5 w-3.5 text-green-500" />
  ) : (
    <X className="h-3.5 w-3.5 text-muted-foreground/50" />
  )
}

function NumericCell({ value, warn, error }: { value: number; warn?: boolean; error?: boolean }) {
  const cn = error ? 'text-destructive font-medium' : warn ? 'text-yellow-600 dark:text-yellow-400' : ''
  return <span className={cn}>{value}</span>
}

// --- Column definitions ---

const meta = (group: ColumnGroup, groupLabel: string, sticky?: boolean) => ({
  group,
  groupLabel,
  ...(sticky ? { sticky: true } : {}),
})

export const pageColumns: ColumnDef<CrawlPage, unknown>[] = [
  // === Core ===
  {
    accessorKey: 'url',
    header: ({ column }) => <SortableHeader column={column}>URL</SortableHeader>,
    cell: ({ row }) => (
      <div className="font-mono text-xs truncate max-w-[400px]" title={row.getValue('url') as string}>
        {row.getValue('url') as string}
      </div>
    ),
    meta: meta('core', 'Core', true),
    size: 400,
  },
  {
    accessorKey: 'status_code',
    header: ({ column }) => <SortableHeader column={column}>Status</SortableHeader>,
    cell: ({ row }) => {
      const code = row.getValue('status_code') as number
      // #25b — redirect_inbound is a SQLite json_group_array of {from, code} objects
      // representing URLs that redirected to this page. Empty/null = no inbound redirects.
      const inboundRaw = row.original.redirect_inbound
      let inbound: Array<{ from: string; code: number }> = []
      if (inboundRaw) {
        try {
          const parsed = JSON.parse(inboundRaw)
          if (Array.isArray(parsed)) inbound = parsed.filter((r) => r && typeof r === 'object')
        } catch { /* ignore malformed JSON */ }
      }
      return (
        <div className="flex items-center gap-1">
          <StatusBadge code={code} />
          {inbound.length > 0 && (
            <span
              className="text-xs text-muted-foreground cursor-help select-none"
              title={`Redirected here from:\n${inbound.map((r) => `  ${r.code} ${r.from}`).join('\n')}`}
              aria-label={`Redirected here from ${inbound.length} URL${inbound.length > 1 ? 's' : ''}`}
            >
              ↪
            </span>
          )}
        </div>
      )
    },
    meta: meta('core', 'Core'),
    size: 90,
  },
  {
    accessorKey: 'response_time_ms',
    header: ({ column }) => <SortableHeader column={column}>Time (ms)</SortableHeader>,
    cell: ({ row }) => {
      const ms = row.getValue('response_time_ms') as number
      return <NumericCell value={ms} warn={ms > 1000} error={ms > 2000} />
    },
    meta: meta('core', 'Core'),
    size: 80,
  },
  {
    accessorKey: 'content_type',
    header: ({ column }) => <SortableHeader column={column}>Type</SortableHeader>,
    cell: ({ row }) => {
      const ct = row.getValue('content_type') as string | null
      return <span className="text-muted-foreground">{ct?.replace('text/html', 'html')?.split(';')[0] || '-'}</span>
    },
    meta: meta('core', 'Core'),
    size: 80,
  },

  // === Title ===
  {
    accessorKey: 'title',
    header: ({ column }) => <SortableHeader column={column}>Title</SortableHeader>,
    cell: ({ row }) => {
      const title = row.getValue('title') as string | null
      const decoded = title ? decodeHtmlEntities(title) : null
      return decoded ? (
        <div className="truncate max-w-[250px]" title={decoded}>
          {decoded}
        </div>
      ) : (
        <span className="text-destructive text-xs">Missing</span>
      )
    },
    meta: meta('title', 'Title'),
    size: 250,
  },
  {
    accessorKey: 'title_length',
    header: ({ column }) => <SortableHeader column={column}>Title Len</SortableHeader>,
    cell: ({ row }) => {
      const len = row.getValue('title_length') as number
      return <NumericCell value={len} warn={len > 0 && len < 30} error={len > 60 || len === 0} />
    },
    meta: meta('title', 'Title'),
    size: 70,
  },

  // === Description ===
  {
    accessorKey: 'meta_description',
    header: ({ column }) => <SortableHeader column={column}>Description</SortableHeader>,
    cell: ({ row }) => {
      const desc = row.getValue('meta_description') as string | null
      const decoded = desc ? decodeHtmlEntities(desc) : null
      return decoded ? (
        <div className="truncate max-w-[250px]" title={decoded}>
          {decoded}
        </div>
      ) : (
        <span className="text-destructive text-xs">Missing</span>
      )
    },
    meta: meta('description', 'Description'),
    size: 250,
  },
  {
    accessorKey: 'meta_description_length',
    header: ({ column }) => <SortableHeader column={column}>Desc Len</SortableHeader>,
    cell: ({ row }) => {
      const len = row.getValue('meta_description_length') as number
      return <NumericCell value={len} warn={len === 0} error={len > 160} />
    },
    meta: meta('description', 'Description'),
    size: 70,
  },

  // === Canonical ===
  {
    accessorKey: 'canonical_status',
    header: ({ column }) => <SortableHeader column={column}>Canonical</SortableHeader>,
    cell: ({ row }) => {
      const status = row.getValue('canonical_status') as string
      const cn =
        status === 'self'
          ? 'text-green-600 dark:text-green-400'
          : status === 'different'
            ? 'text-yellow-600 dark:text-yellow-400'
            : 'text-destructive'
      return <span className={`text-xs font-medium ${cn}`}>{status}</span>
    },
    meta: meta('canonical', 'Canonical'),
    size: 85,
  },
  {
    accessorKey: 'canonical_url',
    header: ({ column }) => <SortableHeader column={column}>Canonical URL</SortableHeader>,
    cell: ({ row }) => {
      const url = row.getValue('canonical_url') as string | null
      return url ? (
        <div className="font-mono text-xs truncate max-w-[250px]" title={url}>
          {url}
        </div>
      ) : (
        <span className="text-muted-foreground">-</span>
      )
    },
    meta: meta('canonical', 'Canonical'),
    size: 250,
  },

  // === Headings ===
  {
    accessorKey: 'h1_count',
    header: ({ column }) => <SortableHeader column={column}>H1s</SortableHeader>,
    cell: ({ row }) => {
      const count = row.getValue('h1_count') as number
      return <NumericCell value={count} error={count === 0} warn={count > 1} />
    },
    meta: meta('headings', 'Headings'),
    size: 50,
  },
  {
    accessorKey: 'h1_texts',
    header: 'H1 Text',
    cell: ({ row }) => {
      const raw = row.getValue('h1_texts') as string | null
      if (!raw) return <span className="text-muted-foreground">-</span>
      try {
        const arr = JSON.parse(raw) as string[]
        const text = arr.join(', ')
        return (
          <div className="truncate max-w-[200px]" title={text}>
            {text}
          </div>
        )
      } catch {
        return <span className="text-muted-foreground">-</span>
      }
    },
    meta: meta('headings', 'Headings'),
    size: 200,
  },
  {
    accessorKey: 'h2_count',
    header: ({ column }) => <SortableHeader column={column}>H2s</SortableHeader>,
    meta: meta('headings', 'Headings'),
    size: 50,
  },
  {
    accessorKey: 'h3_count',
    header: ({ column }) => <SortableHeader column={column}>H3s</SortableHeader>,
    meta: meta('headings', 'Headings'),
    size: 50,
  },
  {
    accessorKey: 'h4_count',
    header: 'H4s',
    meta: meta('headings', 'Headings'),
    size: 50,
  },
  {
    accessorKey: 'h5_count',
    header: 'H5s',
    meta: meta('headings', 'Headings'),
    size: 50,
  },
  {
    accessorKey: 'h6_count',
    header: 'H6s',
    meta: meta('headings', 'Headings'),
    size: 50,
  },
  {
    accessorKey: 'heading_hierarchy_valid',
    header: ({ column }) => <SortableHeader column={column}>Headings OK</SortableHeader>,
    cell: ({ row }) => <BoolIcon value={row.getValue('heading_hierarchy_valid') as number} />,
    meta: meta('headings', 'Headings'),
    size: 80,
  },

  // === Scoring ===
  {
    accessorKey: 'seo_score',
    header: ({ column }) => <SortableHeader column={column}>SEO Score</SortableHeader>,
    cell: ({ row }) => {
      const score = row.getValue('seo_score') as number | null
      if (score === null || score === undefined) return <span className="text-muted-foreground">—</span>
      const cn = `${scoreColor(score)} ${score >= 80 || score < 50 ? 'font-medium' : ''}`
      return <span className={cn}>{score}</span>
    },
    meta: meta('core', 'Core'),
    size: 75,
  },
  {
    accessorKey: 'inrank',
    header: ({ column }) => <SortableHeader column={column}>InRank</SortableHeader>,
    cell: ({ row }) => {
      const rank = row.getValue('inrank') as number | null
      if (rank === null || rank === undefined) return <span className="text-muted-foreground">—</span>
      const cn = `${scoreColor(rank, { good: 7, ok: 4 })} ${rank >= 7 ? 'font-medium' : ''}`
      return <span className={cn}>{rank.toFixed(1)}</span>
    },
    meta: meta('core', 'Core'),
    size: 65,
  },

  // === Content & Links ===
  {
    accessorKey: 'word_count',
    header: ({ column }) => <SortableHeader column={column}>Words</SortableHeader>,
    cell: ({ row }) => {
      const wc = row.getValue('word_count') as number
      return <NumericCell value={wc} warn={wc > 0 && wc < 200} />
    },
    meta: meta('content', 'Content'),
    size: 65,
  },
  {
    accessorKey: 'internal_links_count',
    header: ({ column }) => <SortableHeader column={column}>Int Links</SortableHeader>,
    cell: ({ row }) => {
      const count = row.getValue('internal_links_count') as number
      return <NumericCell value={count} warn={count > 100} />
    },
    meta: meta('content', 'Content'),
    size: 75,
  },
  {
    accessorKey: 'external_links_count',
    header: ({ column }) => <SortableHeader column={column}>Ext Links</SortableHeader>,
    meta: meta('content', 'Content'),
    size: 75,
  },

  // === Images ===
  {
    accessorKey: 'images_count',
    header: ({ column }) => <SortableHeader column={column}>Images</SortableHeader>,
    meta: meta('images', 'Images'),
    size: 65,
  },
  {
    accessorKey: 'images_missing_alt',
    header: ({ column }) => <SortableHeader column={column}>No Alt</SortableHeader>,
    cell: ({ row }) => {
      const count = row.getValue('images_missing_alt') as number
      return <NumericCell value={count} error={count > 0} />
    },
    meta: meta('images', 'Images'),
    size: 60,
  },

  // === Structured Data ===
  {
    accessorKey: 'has_schema',
    header: ({ column }) => <SortableHeader column={column}>Schema</SortableHeader>,
    cell: ({ row }) => <BoolIcon value={row.getValue('has_schema') as number} />,
    meta: meta('structured', 'Structured Data'),
    size: 65,
  },
  {
    accessorKey: 'schema_types',
    header: 'Schema Types',
    cell: ({ row }) => {
      const raw = row.getValue('schema_types') as string | null
      if (!raw) return <span className="text-muted-foreground">-</span>
      try {
        return (
          <div className="truncate max-w-[150px]" title={raw}>
            {(JSON.parse(raw) as string[]).join(', ')}
          </div>
        )
      } catch {
        return <span className="text-muted-foreground">-</span>
      }
    },
    meta: meta('structured', 'Structured Data'),
    size: 150,
  },
  {
    accessorKey: 'has_og_tags',
    header: ({ column }) => <SortableHeader column={column}>OG</SortableHeader>,
    cell: ({ row }) => <BoolIcon value={row.getValue('has_og_tags') as number} />,
    meta: meta('structured', 'Structured Data'),
    size: 50,
  },

  // === Social (OG + Twitter) ===
  {
    accessorKey: 'og_title',
    header: 'OG Title',
    cell: ({ row }) => {
      const v = row.getValue('og_title') as string | null
      const decoded = v ? decodeHtmlEntities(v) : null
      return decoded ? (
        <div className="truncate max-w-[200px]" title={decoded}>{decoded}</div>
      ) : (
        <span className="text-muted-foreground">-</span>
      )
    },
    meta: meta('social', 'Social'),
    size: 200,
  },
  {
    accessorKey: 'og_description',
    header: 'OG Desc',
    cell: ({ row }) => {
      const v = row.getValue('og_description') as string | null
      const decoded = v ? decodeHtmlEntities(v) : null
      return decoded ? (
        <div className="truncate max-w-[200px]" title={decoded}>{decoded}</div>
      ) : (
        <span className="text-muted-foreground">-</span>
      )
    },
    meta: meta('social', 'Social'),
    size: 200,
  },
  {
    accessorKey: 'og_image',
    header: 'OG Image',
    cell: ({ row }) => {
      const v = row.getValue('og_image') as string | null
      return v ? (
        <div className="font-mono truncate max-w-[200px]" title={v}>{v}</div>
      ) : (
        <span className="text-muted-foreground">-</span>
      )
    },
    meta: meta('social', 'Social'),
    size: 200,
  },
  {
    accessorKey: 'twitter_card',
    header: 'Twitter Card',
    cell: ({ row }) => {
      const v = row.getValue('twitter_card') as string | null
      return v || <span className="text-muted-foreground">-</span>
    },
    meta: meta('social', 'Social'),
    size: 100,
  },

  // === Indexing ===
  {
    accessorKey: 'meta_robots_noindex',
    header: ({ column }) => <SortableHeader column={column}>Noindex</SortableHeader>,
    cell: ({ row }) => {
      const v = row.getValue('meta_robots_noindex') as number
      return v ? <Badge variant="destructive">noindex</Badge> : null
    },
    meta: meta('indexing', 'Indexing'),
    size: 75,
  },
  {
    accessorKey: 'meta_robots_nofollow',
    header: 'Nofollow',
    cell: ({ row }) => {
      const v = row.getValue('meta_robots_nofollow') as number
      return v ? <Badge variant="secondary">nofollow</Badge> : null
    },
    meta: meta('indexing', 'Indexing'),
    size: 75,
  },
  {
    accessorKey: 'has_viewport',
    header: ({ column }) => <SortableHeader column={column}>Viewport</SortableHeader>,
    cell: ({ row }) => <BoolIcon value={row.getValue('has_viewport') as number} />,
    meta: meta('indexing', 'Indexing'),
    size: 70,
  },

  // === Security ===
  {
    accessorKey: 'mixed_content_count',
    header: ({ column }) => <SortableHeader column={column}>Mixed</SortableHeader>,
    cell: ({ row }) => {
      const count = row.getValue('mixed_content_count') as number
      return <NumericCell value={count} error={count > 0} />
    },
    meta: meta('security', 'Security'),
    size: 60,
  },

  // === Redirect ===
  {
    accessorKey: 'redirect_url',
    header: 'Redirect To',
    cell: ({ row }) => {
      const v = row.getValue('redirect_url') as string | null
      if (!v) return <span className="text-muted-foreground">-</span>
      // Hide trailing-slash-only redirects — they're URL normalisation, not a real redirect
      const pageUrl = row.getValue('url') as string
      if (pageUrl && stripTrailingSlash(v) === stripTrailingSlash(pageUrl)) {
        return <span className="text-muted-foreground" title={`${pageUrl} → ${v} (trailing-slash normalisation)`}>trailing /</span>
      }
      return <div className="font-mono text-xs truncate max-w-[250px]" title={v}>{v}</div>
    },
    meta: meta('redirect', 'Redirect'),
    size: 250,
  },

  // === i18n ===
  {
    accessorKey: 'content_language',
    header: 'Lang',
    cell: ({ row }) => {
      const v = row.getValue('content_language') as string | null
      return v || <span className="text-muted-foreground">-</span>
    },
    meta: meta('i18n', 'i18n'),
    size: 60,
  },
  {
    accessorKey: 'hreflang_tags',
    header: 'Hreflang',
    cell: ({ row }) => {
      const raw = row.getValue('hreflang_tags') as string | null
      if (!raw) return <span className="text-muted-foreground">-</span>
      try {
        const tags = JSON.parse(raw) as Array<{ lang: string }>
        return (
          <div className="truncate max-w-[150px]" title={raw}>
            {tags.map((t) => t.lang).join(', ')}
          </div>
        )
      } catch {
        return <span className="text-muted-foreground">-</span>
      }
    },
    meta: meta('i18n', 'i18n'),
    size: 150,
  },

  // === Lighthouse / Core Web Vitals ===
  {
    accessorKey: 'psi_performance',
    header: ({ column }) => <SortableHeader column={column}>Perf</SortableHeader>,
    cell: ({ row }) => {
      const score = row.getValue('psi_performance') as number | null
      if (score == null) return <span className="text-muted-foreground">—</span>
      const cn = `${scoreColor(score, { good: 90, ok: 50 })} ${score >= 90 || score < 50 ? 'font-medium' : ''}`
      return <span className={cn}>{score}</span>
    },
    meta: meta('core', 'Core'),
    size: 55,
  },
  {
    accessorKey: 'psi_accessibility',
    header: ({ column }) => <SortableHeader column={column}>A11y</SortableHeader>,
    cell: ({ row }) => {
      const score = row.getValue('psi_accessibility') as number | null
      if (score == null) return <span className="text-muted-foreground">—</span>
      const cn = scoreColor(score, { good: 90, ok: 50 })
      return <span className={cn}>{score}</span>
    },
    meta: meta('core', 'Core'),
    size: 55,
  },
  {
    accessorKey: 'psi_best_practices',
    header: ({ column }) => <SortableHeader column={column}>Best P.</SortableHeader>,
    cell: ({ row }) => {
      const score = row.getValue('psi_best_practices') as number | null
      if (score == null) return <span className="text-muted-foreground">—</span>
      const cn = scoreColor(score, { good: 90, ok: 50 })
      return <span className={cn}>{score}</span>
    },
    meta: meta('core', 'Core'),
    size: 60,
  },
  {
    accessorKey: 'psi_seo',
    header: ({ column }) => <SortableHeader column={column}>LH SEO</SortableHeader>,
    cell: ({ row }) => {
      const score = row.getValue('psi_seo') as number | null
      if (score == null) return <span className="text-muted-foreground">—</span>
      const cn = scoreColor(score, { good: 90, ok: 50 })
      return <span className={cn}>{score}</span>
    },
    meta: meta('core', 'Core'),
    size: 60,
  },
  {
    accessorKey: 'psi_lcp',
    header: ({ column }) => <SortableHeader column={column}>LCP (ms)</SortableHeader>,
    cell: ({ row }) => {
      const v = row.getValue('psi_lcp') as number | null
      if (v == null) return <span className="text-muted-foreground">—</span>
      return <NumericCell value={Math.round(v)} warn={v > 2500} error={v > 4000} />
    },
    meta: meta('core', 'Core'),
    size: 75,
  },
  {
    accessorKey: 'psi_cls',
    header: ({ column }) => <SortableHeader column={column}>CLS</SortableHeader>,
    cell: ({ row }) => {
      const v = row.getValue('psi_cls') as number | null
      if (v == null) return <span className="text-muted-foreground">—</span>
      const cn = v > 0.25 ? 'text-destructive font-medium' : v > 0.1 ? 'text-yellow-600 dark:text-yellow-400' : ''
      return <span className={cn}>{v.toFixed(3)}</span>
    },
    meta: meta('core', 'Core'),
    size: 60,
  },

  // === Crawled At ===
  {
    accessorKey: 'crawled_at',
    header: ({ column }) => <SortableHeader column={column}>Crawled</SortableHeader>,
    cell: ({ row }) => {
      const iso = row.getValue('crawled_at') as string
      return (
        <span className="text-muted-foreground text-xs">
          {new Date(iso).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        </span>
      )
    },
    meta: meta('core', 'Core'),
    size: 85,
  },
]

// --- Default column visibility ---

export const DEFAULT_COLUMN_VISIBILITY: VisibilityState = {
  // Visible by default
  url: true,
  status_code: true,
  seo_score: true,
  inrank: true,
  response_time_ms: true,
  title: true,
  title_length: true,
  meta_description_length: true,
  canonical_status: true,
  h1_count: true,
  word_count: true,
  internal_links_count: true,
  external_links_count: true,
  images_count: true,
  images_missing_alt: true,
  has_schema: true,
  has_og_tags: true,
  meta_robots_noindex: true,
  crawled_at: true,
  psi_performance: true,
  // Hidden by default
  psi_accessibility: false,
  psi_best_practices: false,
  psi_seo: false,
  psi_lcp: false,
  psi_cls: false,
  content_type: false,
  meta_description: false,
  canonical_url: false,
  h1_texts: false,
  h2_count: false,
  h3_count: false,
  h4_count: false,
  h5_count: false,
  h6_count: false,
  heading_hierarchy_valid: false,
  schema_types: false,
  og_title: false,
  og_description: false,
  og_image: false,
  twitter_card: false,
  meta_robots_nofollow: false,
  has_viewport: false,
  mixed_content_count: false,
  redirect_url: false,
  content_language: false,
  hreflang_tags: false,
  images_data: false,
}

// Group labels for the column visibility dropdown
export const COLUMN_GROUPS: Array<{ key: ColumnGroup; label: string }> = [
  { key: 'core', label: 'Core' },
  { key: 'title', label: 'Title' },
  { key: 'description', label: 'Description' },
  { key: 'canonical', label: 'Canonical' },
  { key: 'headings', label: 'Headings' },
  { key: 'content', label: 'Content & Links' },
  { key: 'images', label: 'Images' },
  { key: 'structured', label: 'Structured Data' },
  { key: 'social', label: 'Social' },
  { key: 'indexing', label: 'Indexing' },
  { key: 'security', label: 'Security' },
  { key: 'redirect', label: 'Redirect' },
  { key: 'i18n', label: 'Internationalisation' },
]
